import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import {
  ChevronLeft, Diamond, Printer, Share2, Calendar, User,
  Download, Send, Plus, Trash2, CheckCircle2, Clock,
  AlertCircle, X, Search, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { generateDiamondSeriesPdf } from '../utils/diamondSeriesPdf';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(str) {
  if (!str) return '';
  const d = new Date(str.includes('T') ? str : str + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_CONFIG = {
  saved:       { label: 'Saved',       bg: 'bg-gray-100',    text: 'text-gray-600',  icon: Clock },
  allocated:   { label: 'Allocated',   bg: 'bg-amber-100',   text: 'text-amber-700', icon: Send },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100',    text: 'text-blue-700',  icon: AlertCircle },
  completed:   { label: 'Completed',   bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.saved;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

// ─── Allocate Drawer ──────────────────────────────────────────────────────────
function AllocateDrawer({ open, onClose, onAllocate }) {
  const [supervisors, setSupervisors] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/diamond-series/users/stock-supervisors')
      .then(r => setSupervisors(r.data))
      .catch(() => toast.error('Failed to load supervisors'));
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedId) { toast.error('Select a stock supervisor'); return; }
    setSaving(true);
    try {
      await onAllocate(selectedId, note);
      setSelectedId(''); setNote('');
      onClose();
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">Allocate to Stock Supervisor</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Stock Supervisor <span className="text-red-500">*</span></label>
            <select
              data-testid="ss-select"
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select supervisor...</option>
              {supervisors.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Note (optional)</label>
            <textarea
              data-testid="alloc-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any instructions for the supervisor..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button
            data-testid="confirm-allocate-btn"
            onClick={handleSubmit}
            disabled={saving || !selectedId}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto" /> : <><Send className="w-4 h-4 mr-2" />Allocate & Notify</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Order Form Dialog ────────────────────────────────────────────────────
function AddOrderFormDialog({ open, onClose, onAdd, grandTotalWt, assignedWt }) {
  const [formNumber, setFormNumber] = useState('');
  const [carats, setCarats] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  const remaining = Math.max(0, grandTotalWt - assignedWt);

  useEffect(() => {
    if (!open) { setFormNumber(''); setCarats(''); setSearchQ(''); setSearchResults([]); setSelectedOrder(null); }
  }, [open]);

  const handleSearch = (q) => {
    setSearchQ(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/diamond-series/orders/search?q=${encodeURIComponent(q)}`);
        setSearchResults(r.data);
      } catch { setSearchResults([]); }
    }, 300);
  };

  const pickOrder = (ord) => {
    setSelectedOrder(ord);
    setFormNumber(ord.order_number);
    setSearchQ('');
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (!formNumber.trim()) { toast.error('Enter a form number'); return; }
    const ct = parseFloat(carats);
    if (!ct || ct <= 0) { toast.error('Enter a valid carat weight'); return; }
    setSaving(true);
    try {
      await onAdd({
        form_number: formNumber.trim(),
        order_id: selectedOrder?.id || '',
        order_title: selectedOrder?.title || '',
        carats_assigned: ct,
      });
      onClose();
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">Add Order Form</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="bg-blue-50 rounded-xl px-4 py-2.5 mb-4 flex gap-6 text-sm">
          <div><p className="text-xs text-blue-500">Available</p><p className="font-bold text-blue-800">{remaining.toFixed(3)} ct</p></div>
          <div><p className="text-xs text-blue-500">Total Series</p><p className="font-bold text-blue-800">{grandTotalWt.toFixed(3)} ct</p></div>
        </div>

        <div className="space-y-4">
          {/* Order search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Search Existing Order</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="order-search-input"
                value={searchQ}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by order number or title..."
                className="pl-9 h-10 text-sm"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto shadow-md">
                {searchResults.map(r => (
                  <button
                    key={r.id}
                    data-testid={`order-result-${r.id}`}
                    onClick={() => pickOrder(r)}
                    className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                  >
                    <p className="font-semibold text-gray-900 text-sm">{r.order_number}</p>
                    {r.title && <p className="text-xs text-gray-500 truncate">{r.title}</p>}
                  </button>
                ))}
              </div>
            )}
            {selectedOrder && (
              <div className="mt-1.5 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-sm font-medium text-emerald-800">{selectedOrder.order_number}</span>
                <button onClick={() => { setSelectedOrder(null); setFormNumber(''); }} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>

          {/* Manual form number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Form Number <span className="text-red-500">*</span></label>
            <Input
              data-testid="order-form-number"
              value={formNumber}
              onChange={e => setFormNumber(e.target.value)}
              placeholder="e.g. Ord.001.078 or custom number"
              className="h-10 text-sm"
            />
          </div>

          {/* Carats */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Carats Assigned <span className="text-red-500">*</span></label>
            <Input
              data-testid="order-carats"
              type="number"
              inputMode="decimal"
              step="0.001"
              min="0.001"
              value={carats}
              onChange={e => setCarats(e.target.value)}
              placeholder="0.000"
              className="h-10 text-sm"
            />
            {parseFloat(carats) > remaining && remaining > 0 && (
              <p className="text-xs text-amber-600 mt-1">Exceeds available ({remaining.toFixed(3)} ct)</p>
            )}
          </div>

          <Button
            data-testid="add-order-form-btn"
            onClick={handleSubmit}
            disabled={saving || !formNumber.trim() || !carats}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto" /> : <><Plus className="w-4 h-4 mr-2" />Add Order Form</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Detail Page ─────────────────────────────────────────────────────────
export default function DiamondSeriesDetail() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const role = currentUser?.role;

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [completing, setCompleting] = useState(false);

  const canAllocate = ['admin', 'production_supervisor', 'diamond_dept'].includes(role);
  const canAddOrderForm = ['stock_supervisor', 'admin'].includes(role);
  const canComplete = ['admin', 'inventory_manager'].includes(role);
  const isMyAllocation = role === 'stock_supervisor' && series?.allocated_to_id === currentUser?.id?.toString();

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/diamond-series/${seriesId}`);
      setSeries(res.data);
    } catch (e) {
      toast.error('Failed to load series');
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => { load(); }, [load]);

  const handleAllocate = async (ssId, note) => {
    const res = await api.post(`/diamond-series/${seriesId}/allocate`, {
      allocated_to_id: ssId,
      allocation_note: note,
    });
    setSeries(res.data);
    toast.success(`Allocated & notification sent to ${res.data.allocated_to_name}`);
  };

  const handleAddOrderForm = async (payload) => {
    const res = await api.post(`/diamond-series/${seriesId}/order-forms`, payload);
    setSeries(res.data);
    toast.success('Order form added');
  };

  const handleRemoveOrderForm = async (formId) => {
    if (!window.confirm('Remove this order form?')) return;
    const res = await api.delete(`/diamond-series/${seriesId}/order-forms/${formId}`);
    setSeries(res.data);
    toast.success('Removed');
  };

  const handleComplete = async () => {
    if (!window.confirm('Mark this series as Completed? This cannot be undone.')) return;
    setCompleting(true);
    try {
      const res = await api.patch(`/diamond-series/${seriesId}/complete`);
      setSeries(res.data);
      toast.success('Series marked as Completed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setCompleting(false); }
  };

  const handlePrint = () => window.print();
  const handleDownloadPdf = () => {
    if (!series) return;
    try { generateDiamondSeriesPdf(series); toast.success('PDF downloaded'); }
    catch (e) { toast.error('PDF generation failed'); }
  };
  const handleShare = async () => {
    if (!series) return;
    const text = buildShareText(series);
    if (navigator.share) {
      try { await navigator.share({ title: `Diamond Series ${series.series_number}`, text }); }
      catch (e) { if (e.name !== 'AbortError') toast.error('Share failed'); }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    }
  };

  // Usage calculations
  const assignedWt = (series?.order_forms || []).reduce((s, f) => s + (f.carats_assigned || 0), 0);
  const totalWt = series?.grand_total_weight_ct || 0;
  const usedPct = totalWt > 0 ? Math.min(100, (assignedWt / totalWt) * 100) : 0;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }
  if (!series) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4"><Diamond className="w-12 h-12 text-gray-300" /><p className="text-gray-500">Series not found</p><Button onClick={() => navigate('/diamond-series')}>Back to list</Button></div>;
  }

  const isCompleted = series.status === 'completed';

  return (
    <>
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>

      <AllocateDrawer open={allocateOpen} onClose={() => setAllocateOpen(false)} onAllocate={handleAllocate} />
      <AddOrderFormDialog
        open={addFormOpen}
        onClose={() => setAddFormOpen(false)}
        onAdd={handleAddOrderForm}
        grandTotalWt={totalWt}
        assignedWt={assignedWt}
      />

      <div className="min-h-screen bg-gray-50">
        {/* App Header */}
        <div className="no-print bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Diamond className="w-4 h-4 text-blue-600 shrink-0" />
              <h1 className="text-base font-semibold text-gray-900 truncate">{series.series_number}</h1>
              <StatusChip status={series.status} />
            </div>
            <button data-testid="share-btn" onClick={handleShare} className="p-2 rounded-full hover:bg-gray-100 text-gray-600"><Share2 className="w-4 h-4" /></button>
            <button data-testid="download-pdf-btn" onClick={handleDownloadPdf} className="p-2 rounded-full hover:bg-gray-100 text-blue-600"><Download className="w-4 h-4" /></button>
            <button data-testid="print-btn" onClick={handlePrint} className="p-2 rounded-full hover:bg-gray-100 text-gray-600"><Printer className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

          {/* Action Required banner — SS only */}
          {isMyAllocation && (series.status === 'allocated' || series.status === 'in_progress') && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3 no-print">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm">Action Required</p>
                <p className="text-amber-700 text-xs mt-0.5">This series has been allocated to you. Add order form numbers below.</p>
                {series.allocation_note && <p className="text-amber-600 text-xs mt-1.5 italic">"{series.allocation_note}"</p>}
              </div>
              <button
                data-testid="add-order-form-action-btn"
                onClick={() => setAddFormOpen(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors shrink-0"
              >
                + Add Form
              </button>
            </div>
          )}

          {/* Series header card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-2xl font-bold text-blue-700">{series.series_number}</span>
                  {series.manual_series_no && <span className="text-gray-400">#{series.manual_series_no}</span>}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {series.colour && <span className="font-semibold text-gray-800 text-lg">{series.colour}</span>}
                  {series.clarity?.map(c => <span key={c} className="text-sm bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{c}</span>)}
                  {series.shade && <span className="text-sm bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{series.shade}</span>}
                  {series.shape_type_name && <span className="text-sm border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{series.shape_type_name}</span>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500"><Calendar className="w-4 h-4" />{formatDate(series.date_of_issue)}</div>
              <div className="flex items-center gap-2 text-sm text-gray-500"><User className="w-4 h-4" />{series.submitted_by_name}</div>
              {series.codes_used && <div className="col-span-2 text-sm text-gray-500">Codes: {series.codes_used}</div>}
            </div>

            {/* Allocation info */}
            {series.allocated_to_name && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm">
                <Send className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-gray-500">Allocated to</span>
                <span className="font-semibold text-blue-700">{series.allocated_to_name}</span>
                <span className="text-gray-400 text-xs ml-auto">{formatDate(series.allocated_at)}</span>
              </div>
            )}
            {isCompleted && (
              <div className="mt-2 flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed by {series.completed_by_name} on {formatDate(series.completed_at)}</span>
              </div>
            )}
          </div>

          {/* Grand Total card */}
          <div className="bg-slate-900 text-white rounded-2xl p-5">
            <p className="text-slate-400 text-sm mb-2">Grand Total</p>
            <div className="flex items-center gap-8">
              <div><p className="text-3xl font-bold">{Number(series.grand_total_weight_ct).toFixed(3)}</p><p className="text-slate-400 text-sm">carats</p></div>
              <div><p className="text-3xl font-bold">{Math.round(series.grand_total_pieces).toLocaleString()}</p><p className="text-slate-400 text-sm">pieces</p></div>
            </div>
          </div>

          {/* ── Usage tracker ── */}
          {(series.order_forms?.length > 0 || canAddOrderForm) && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm no-print">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">Order Forms & Usage</h3>
                {canAddOrderForm && !isCompleted && (series.status === 'allocated' || series.status === 'in_progress') && (
                  <button
                    data-testid="add-order-form-btn-main"
                    onClick={() => setAddFormOpen(true)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Add Form
                  </button>
                )}
              </div>

              {/* Usage bar */}
              {totalWt > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Assigned: <strong className="text-gray-900">{assignedWt.toFixed(3)} ct</strong></span>
                    <span>Remaining: <strong className={assignedWt > totalWt ? 'text-red-600' : 'text-emerald-600'}>{Math.max(0, totalWt - assignedWt).toFixed(3)} ct</strong></span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${usedPct >= 100 ? 'bg-emerald-500' : usedPct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, usedPct)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-right mt-1">{usedPct.toFixed(1)}% used</p>
                </div>
              )}

              {/* Order forms list */}
              {series.order_forms?.length > 0 ? (
                <div className="space-y-2">
                  {series.order_forms.map((f, i) => (
                    <div key={f.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-sm">{f.form_number}</span>
                          {f.order_title && <span className="text-xs text-gray-500 truncate">— {f.order_title}</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                          <span className="font-medium text-blue-700">{Number(f.carats_assigned).toFixed(3)} ct</span>
                          <span>by {f.added_by_name}</span>
                          <span>{formatDate(f.added_at)}</span>
                        </div>
                      </div>
                      {canAddOrderForm && !isCompleted && (
                        <button
                          data-testid={`remove-form-${f.id}`}
                          onClick={() => handleRemoveOrderForm(f.id)}
                          className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No order forms added yet</p>
              )}
            </div>
          )}

          {/* Allocate button — production/admin on saved or reallocate */}
          {canAllocate && !isCompleted && (
            <button
              data-testid="allocate-btn"
              onClick={() => setAllocateOpen(true)}
              className="w-full py-3.5 rounded-2xl border-2 border-blue-300 text-blue-600 font-semibold text-sm hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 no-print"
            >
              <Send className="w-4 h-4" />
              {series.allocated_to_name ? `Re-allocate (currently: ${series.allocated_to_name})` : 'Allocate to Stock Supervisor'}
            </button>
          )}

          {/* Mark Complete — inventory manager / admin */}
          {canComplete && (series.status === 'in_progress' || series.status === 'allocated') && (
            <button
              data-testid="complete-btn"
              onClick={handleComplete}
              disabled={completing}
              className="w-full py-3.5 rounded-2xl border-2 border-emerald-300 text-emerald-700 font-semibold text-sm hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 no-print"
            >
              {completing
                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" />
                : <><CheckCircle2 className="w-4 h-4" />Mark as Completed</>
              }
            </button>
          )}

          {/* Categories */}
          {series.categories?.map((cat, ci) => {
            const totalCatWt = cat.total_weight_ct ?? cat.sizes?.reduce((s, r) => s + (r.weight_ct || 0), 0) ?? 0;
            const totalCatPcs = cat.total_pieces ?? cat.sizes?.reduce((s, r) => s + (r.pieces || 0), 0) ?? 0;
            return (
              <div key={ci} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-blue-50 px-4 py-3 flex justify-between items-center">
                  <h3 className="font-bold text-blue-900 text-base">{cat.category_name}</h3>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-blue-800">{Number(totalCatWt).toFixed(3)} ct</span>
                    <span className="text-blue-600">{Math.round(totalCatPcs).toLocaleString()} pcs</span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-4 py-2 text-gray-500 font-medium">Sieve Size</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-medium">Avg (ct)</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-medium">Weight (ct)</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-medium">Pieces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cat.sizes?.map((s, si) => (
                        <tr key={si} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 font-medium text-gray-900">{s.sieve_size_label}</td>
                          <td className="px-4 py-2.5 text-right text-gray-500">{s.avg_weight_ct}</td>
                          <td className="px-4 py-2.5 text-right font-semibold">{Number(s.weight_ct).toFixed(3)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-blue-700">{Math.round(s.pieces).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-blue-50 font-bold">
                        <td colSpan={2} className="px-4 py-2.5 text-blue-800">Category Total</td>
                        <td className="px-4 py-2.5 text-right text-blue-800">{Number(totalCatWt).toFixed(3)}</td>
                        <td className="px-4 py-2.5 text-right text-blue-800">{Math.round(totalCatPcs).toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}

          <div className="h-8 no-print" />
        </div>
      </div>
    </>
  );
}

function buildShareText(series) {
  const lines = [
    `*Diamond Series: ${series.series_number}*`,
    series.manual_series_no ? `Series No: ${series.manual_series_no}` : '',
    `Colour: ${series.colour}  Clarity: ${series.clarity?.join('/')}  Shade: ${series.shade}`,
    series.shape_type_name ? `Shape: ${series.shape_type_name}` : '',
    series.codes_used ? `Codes: ${series.codes_used}` : '',
    `Date: ${series.date_of_issue}`,
    series.allocated_to_name ? `Allocated to: ${series.allocated_to_name}` : '',
    '',
  ].filter(l => l !== null);
  series.categories?.forEach(cat => {
    lines.push(`*${cat.category_name}*`);
    cat.sizes?.forEach(s => {
      lines.push(`  ${s.sieve_size_label}: ${Number(s.weight_ct).toFixed(3)} ct → ${Math.round(s.pieces)} pcs`);
    });
    lines.push(`  Total: ${Number(cat.total_weight_ct ?? 0).toFixed(3)} ct | ${Math.round(cat.total_pieces ?? 0)} pcs`);
    lines.push('');
  });
  lines.push(`*GRAND TOTAL: ${Number(series.grand_total_weight_ct).toFixed(3)} ct | ${Math.round(series.grand_total_pieces).toLocaleString()} pcs*`);
  if (series.order_forms?.length > 0) {
    lines.push('', '*Order Forms:*');
    series.order_forms.forEach(f => lines.push(`  ${f.form_number}: ${Number(f.carats_assigned).toFixed(3)} ct`));
  }
  return lines.join('\n');
}
